import React, { useState, useEffect } from 'react';
import { FaPlus, FaEye, FaTrash, FaList, FaRegMoneyBillAlt, FaUser, FaCheckCircle, FaClock, FaTimesCircle, FaFilePdf, FaDownload, FaTimes, FaInfoCircle, FaEdit } from 'react-icons/fa';
import { Oval } from 'react-loader-spinner';
import { db, auth } from '../config/firebaseConfig';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { useUserProfile } from '../contexts/UserProfileContext';
import styles from './ExpenseRequests.module.css';

const ExpenseRequests = () => {
  const [expenseRequests, setExpenseRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [showEditReasonModal, setShowEditReasonModal] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [editReasonText, setEditReasonText] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingExpenseId, setRejectingExpenseId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const { employeeData, employeeId, userAuthorization } = useUserProfile();

  // Admin kontrolü
  useEffect(() => {
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const usersRef = collection(db, 'Users');
          const [userDocSnap, emailSnapshot, eMailSnapshot] = await Promise.all([
            getDocs(query(usersRef, where('uid', '==', user.uid))),
            getDocs(query(usersRef, where('email', '==', user.email))),
            getDocs(query(usersRef, where('eMail', '==', user.email)))
          ]);

          if (
            (!userDocSnap.empty && userDocSnap.docs[0].data().role === 'admin') ||
            (!emailSnapshot.empty && emailSnapshot.docs[0].data().role === 'admin') ||
            (!eMailSnapshot.empty && eMailSnapshot.docs[0].data().role === 'admin') ||
            (userAuthorization && userAuthorization.includes('admin'))
          ) {
            setIsUserAdmin(true);
          }
        } catch (error) {
          console.error("Error checking admin status:", error);
        }
      }
    };

    checkAdminStatus();
  }, [userAuthorization]);

  useEffect(() => {
    if (isUserAdmin || employeeId) {
      fetchExpenseRequests(true);
    }
  }, [employeeId, isUserAdmin]);

  const fetchExpenseRequests = async (showLoading = false) => {
    if (!employeeId && !isUserAdmin) {
      console.log('Employee ID not available yet:', employeeId);
      return;
    }
    
    try {
      if (showLoading || expenseRequests.length === 0) {
        setIsLoading(true);
      }
      const expenseRequestsRef = collection(db, 'ExpenseRequests');
      
      let querySnapshot;
      if (isUserAdmin) {
        // Super admin tüm talepleri görebilir
        querySnapshot = await getDocs(expenseRequestsRef);
        console.log('Fetching all expense requests for admin');
      } else {
        // Normal kullanıcı sadece kendi taleplerini görebilir
        const q = query(expenseRequestsRef, where('employeeId', '==', employeeId));
        querySnapshot = await getDocs(q);
        console.log('Fetching expense requests for employeeId:', employeeId);
      }
      
      const requests = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('Fetched expense requests:', requests);
      
      // Sort by creation date (newest first)
      requests.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      
      setExpenseRequests(requests);
    } catch (error) {
      console.error('Error fetching expense requests:', error);
      toast.error('Masraf talepleri yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Bu masraf talebini silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'ExpenseRequests', requestId));
      toast.success('Masraf talebi silindi!');
      fetchExpenseRequests();
    } catch (error) {
      console.error('Error deleting expense request:', error);
      toast.error('Masraf talebi silinirken bir hata oluştu: ' + error.message);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    if (!isUserAdmin) {
      toast.error('Bu işlem için yetkiniz bulunmamaktadır.');
      return;
    }

    // If rejecting, show modal for reason
    if (newStatus === 'rejected') {
      setRejectingExpenseId(requestId);
      setShowRejectModal(true);
      return;
    }

    // For approval, proceed directly
    try {
      await updateDoc(doc(db, 'ExpenseRequests', requestId), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
        processedBy: auth.currentUser?.email || 'Admin'
      });
      
      // Update local state
      setExpenseRequests(prev => prev.map(req => 
        req.id === requestId 
          ? { ...req, status: newStatus, updatedAt: new Date().toISOString() }
          : req
      ));
      
      toast.success('Masraf talebi onaylandı!');
    } catch (error) {
      console.error('Error updating request status:', error);
      toast.error('Durum güncellenirken bir hata oluştu: ' + error.message);
    }
  };

  const handleRejectWithReason = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Lütfen reddedilme nedenini giriniz.');
      return;
    }

    if (!isUserAdmin) {
      toast.error('Bu işlem için yetkiniz bulunmamaktadır.');
      return;
    }

    try {
      await updateDoc(doc(db, 'ExpenseRequests', rejectingExpenseId), {
        status: 'rejected',
        rejectionReason: rejectionReason.trim(),
        rejectedAt: new Date().toISOString(),
        rejectedBy: auth.currentUser?.email || 'Admin',
        updatedAt: new Date().toISOString(),
        processedBy: auth.currentUser?.email || 'Admin'
      });

      // Update local state
      setExpenseRequests(prev => prev.map(req => 
        req.id === rejectingExpenseId 
          ? { 
              ...req, 
              status: 'rejected',
              rejectionReason: rejectionReason.trim(),
              rejectedAt: new Date().toISOString(),
              rejectedBy: auth.currentUser?.email || 'Admin',
              updatedAt: new Date().toISOString()
            }
          : req
      ));
      
      toast.success('Masraf talebi reddedildi!');
      setShowRejectModal(false);
      setRejectionReason('');
      setRejectingExpenseId(null);
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Durum güncellenirken bir hata oluştu: ' + error.message);
    }
  };

  const handleCancelReject = () => {
    setShowRejectModal(false);
    setRejectionReason('');
    setRejectingExpenseId(null);
  };

  const handlePdfView = (pdfUrl, fileName) => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    } else {
      toast.error('PDF dosyası bulunamadı');
    }
  };

  const handlePdfDownload = (pdfUrl, fileName) => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName || 'masraf-belgesi.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      toast.error('PDF dosyası bulunamadı');
    }
  };

  const handleViewReason = (reason) => {
    setSelectedReason(reason);
    setShowReasonModal(true);
  };

  const handleEditReasonClick = (expenseId, currentReason) => {
    setEditingExpenseId(expenseId);
    setEditReasonText(currentReason || '');
    setShowEditReasonModal(true);
  };

  const handleUpdateReason = async () => {
    if (!editReasonText.trim()) {
      toast.warn('Lütfen reddedilme nedenini giriniz');
      return;
    }

    if (!isUserAdmin) {
      toast.error('Bu işlem için yetkiniz bulunmamaktadır.');
      return;
    }

    try {
      await updateDoc(doc(db, 'ExpenseRequests', editingExpenseId), {
        rejectionReason: editReasonText.trim(),
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setExpenseRequests(prev => prev.map(req => 
        req.id === editingExpenseId 
          ? { ...req, rejectionReason: editReasonText.trim() }
          : req
      ));

      toast.success('Reddedilme nedeni güncellendi!');
      setShowEditReasonModal(false);
      setEditingExpenseId(null);
      setEditReasonText('');
    } catch (error) {
      console.error('Error updating rejection reason:', error);
      toast.error('Güncelleme sırasında bir hata oluştu: ' + error.message);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Oval
          color="#3d78d7"
          secondaryColor="#93b5e8"
          height={44}
          width={44}
          visible={true}
        />
        <p>Masraf talepleri yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>
            {isUserAdmin ? 'Tüm Masraf Talepleri' : 'Masraf Taleplerim'}
          </h2>
          <span className={styles.requestCount}>{expenseRequests.length} talep</span>
        </div>
        <button
          className={styles.refreshButton}
          onClick={() => fetchExpenseRequests(true)}
          disabled={isLoading}
        >
          <FaEye /> Yenile
        </button>
      </div>

      {expenseRequests.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIconCircle}>
            <FaRegMoneyBillAlt size={28} />
          </div>
          <h3 className={styles.emptyTitle}>Henüz masraf talebiniz bulunmamaktadır</h3>
          <p className={styles.emptyText}>Masraf talebi oluşturmak için "Masraf Talep Et" sekmesini kullanabilirsiniz.</p>
        </div>
      ) : (
        <div className={styles.requestsList}>
          {expenseRequests.map((request) => (
            <div key={request.id} className={`${styles.requestCard} ${styles[`card_${request.status || 'pending'}`]}`}>
              <div className={styles.requestTop}>
                <div className={styles.requestInfo}>
                  <div className={styles.amountRow}>
                    <h3 className={styles.requestAmount}>
                      ₺{request.amount?.toLocaleString('tr-TR') || '0'}
                    </h3>
                    <span className={`${styles.statusBadge} ${styles[request.status || 'pending']}`}>
                      {request.status === 'approved' ? '✓ Onaylandı' : 
                       request.status === 'rejected' ? '✗ Reddedildi' : '● Bekliyor'}
                    </span>
                  </div>
                  <div className={styles.metaRow}>
                    <span className={styles.categoryTag}>{request.category}</span>
                    <span className={styles.dateSeparator}>·</span>
                    <span className={styles.requestDate}>
                      {request.createdAt ? new Date(request.createdAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Tarih yok'}
                    </span>
                    {isUserAdmin && request.employeeName && (
                      <>
                        <span className={styles.dateSeparator}>·</span>
                        <span className={styles.employeeTag}>
                          <FaUser className={styles.employeeIcon} />
                          {request.employeeName}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className={styles.requestActions}>
                  {isUserAdmin && request.status === 'pending' && (
                    <>
                      <button
                        className={styles.approveButton}
                        onClick={() => handleStatusUpdate(request.id, 'approved')}
                        title="Onayla"
                      >
                        <FaCheckCircle />
                      </button>
                      <button
                        className={styles.rejectButton}
                        onClick={() => handleStatusUpdate(request.id, 'rejected')}
                        title="Reddet"
                      >
                        <FaTimesCircle />
                      </button>
                    </>
                  )}
                  {(!isUserAdmin && request.status === 'pending') && (
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDeleteRequest(request.id)}
                      title="Sil"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </div>
              
              {request.description && (
                <p className={styles.requestDescription}>
                  {request.description}
                </p>
              )}

              {/* Rejection Reason */}
              {request.status === 'rejected' && request.rejectionReason && (
                <div className={styles.rejectionReasonSection}>
                  <button 
                    className={styles.viewReasonBtn}
                    onClick={() => handleViewReason(request.rejectionReason)}
                  >
                    <FaInfoCircle /> Nedeni Gör
                  </button>
                  {isUserAdmin && (
                    <button 
                      className={styles.editReasonBtn}
                      onClick={() => handleEditReasonClick(request.id, request.rejectionReason)}
                      title="Nedeni Düzenle"
                    >
                      <FaEdit /> Düzenle
                    </button>
                  )}
                </div>
              )}

              {/* PDF Belge Gösterimi - Çoklu dosya desteği */}
              {(request.pdfUrls?.length > 0 || request.pdfUrl) && (
                <div className={styles.pdfSection}>
                  {/* Yeni format - Çoklu dosyalar */}
                  {request.pdfUrls?.length > 0 ? (
                    <div className={styles.multiplePdfsContainer}>
                      <div className={styles.pdfSectionHeader}>
                        <FaFilePdf className={styles.pdfIcon} />
                        <span className={styles.pdfSectionTitle}>
                          Belgeler ({request.pdfUrls.length})
                        </span>
                      </div>
                      <div className={styles.pdfsList}>
                        {request.pdfUrls.map((pdfUrl, index) => (
                          <div key={index} className={styles.pdfItem}>
                            <div className={styles.pdfInfo}>
                              <FaFilePdf className={styles.pdfItemIcon} />
                              <span className={styles.pdfText}>
                                {request.pdfFileNames?.[index] || `Belge ${index + 1}.pdf`}
                              </span>
                            </div>
                            <div className={styles.pdfActions}>
                              <button
                                className={styles.pdfViewBtn}
                                onClick={() => handlePdfView(pdfUrl, request.pdfFileNames?.[index])}
                                title="PDF'i Görüntüle"
                              >
                                <FaEye />
                              </button>
                              <button
                                className={styles.pdfDownloadBtn}
                                onClick={() => handlePdfDownload(pdfUrl, request.pdfFileNames?.[index])}
                                title="PDF'i İndir"
                              >
                                <FaDownload />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    /* Eski format - Tek dosya (geriye dönük uyumluluk) */
                    <div className={styles.singlePdfContainer}>
                      <div className={styles.pdfInfo}>
                        <FaFilePdf className={styles.pdfIcon} />
                        <span className={styles.pdfText}>
                          {request.pdfFileName || 'Masraf Belgesi.pdf'}
                        </span>
                      </div>
                      <div className={styles.pdfActions}>
                        <button
                          className={styles.pdfViewBtn}
                          onClick={() => handlePdfView(request.pdfUrl, request.pdfFileName)}
                          title="PDF'i Görüntüle"
                        >
                          <FaEye />
                        </button>
                        <button
                          className={styles.pdfDownloadBtn}
                          onClick={() => handlePdfDownload(request.pdfUrl, request.pdfFileName)}
                          title="PDF'i İndir"
                        >
                          <FaDownload />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Rejection Reason Modal */}
      {showReasonModal && (
        <div className={styles.modalOverlay} onClick={() => setShowReasonModal(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Reddedilme Nedeni</h3>
              <button className={styles.modalCloseBtn} onClick={() => setShowReasonModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.rejectionReasonText}>{selectedReason}</p>
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.modalCloseButton} 
                onClick={() => setShowReasonModal(false)}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Rejection Reason Modal */}
      {showEditReasonModal && (
        <div className={styles.modalOverlay} onClick={() => setShowEditReasonModal(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Reddedilme Nedenini Düzenle</h3>
              <button className={styles.modalCloseBtn} onClick={() => setShowEditReasonModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalDescription}>
                Reddedilme nedenini güncelleyiniz:
              </p>
              <textarea
                className={styles.rejectionTextarea}
                value={editReasonText}
                onChange={e => setEditReasonText(e.target.value)}
                placeholder="Reddedilme nedenini buraya yazınız..."
                rows={5}
                autoFocus
              />
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.modalCancelBtn} 
                onClick={() => setShowEditReasonModal(false)}
              >
                İptal
              </button>
              <button 
                className={styles.modalSaveBtn} 
                onClick={handleUpdateReason}
                disabled={!editReasonText.trim()}
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject with Reason Modal */}
      {showRejectModal && (
        <div className={styles.modalOverlay} onClick={handleCancelReject}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Masraf Talebini Reddet</h3>
              <button className={styles.modalCloseBtn} onClick={handleCancelReject}>
                <FaTimes />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalDescription}>
                Lütfen masraf talebini neden reddettiğinizi açıklayın. Bu bilgi çalışana iletilecektir.
              </p>
              <label className={styles.modalLabel}>
                Reddedilme Nedeni <span className={styles.required}>*</span>
              </label>
              <textarea
                className={styles.rejectionTextarea}
                value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)}
                placeholder="Örn: Dekont eksik, tutar hatalı, onaysız harcama..."
                rows={5}
                autoFocus
              />
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.modalCancelBtn} 
                onClick={handleCancelReject}
              >
                İptal
              </button>
              <button 
                className={styles.modalRejectBtn} 
                onClick={handleRejectWithReason}
                disabled={!rejectionReason.trim()}
              >
                Reddet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseRequests;